import { useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import MuiDialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import { withTranslation } from "react-i18next";
import { isNil } from "lodash";

import fpSimulationService from "@plannel/services/fp/fp-simulation-service";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

// 옵션은 tb_fp_simul_option.category_cd에 `FP_OPTN_<SNAKE>` 문자열로 저장된다(공통코드 아님).
// old는 categoryCd에서 접두사를 떼고 camelCase로 바꾼 이름을 폼 필드로 썼다 — 그 대응을 그대로 유지한다.
// (라벨 키만 예외: fcfs 카드의 제목은 FP_OPTN_INFINITE_FCFS, 저장 코드는 FP_OPTN_FCFS.)
const OPTION_FIELDS = ["work", "nonWork", "efficiency", "preBuild", "infiniteTool", "lateNotAllowed", "fcfs"];
const SWITCH_FIELDS = ["preBuild", "infiniteTool", "lateNotAllowed", "fcfs"];

const camelToUpperSnake = (value) => value.replace(/[A-Z]/g, char => `_${char}`).toUpperCase();
const toCategoryCd = (field) => `FP_OPTN_${camelToUpperSnake(field)}`;
const toField = (categoryCd) =>
  categoryCd.replace("FP_OPTN_", "").toLowerCase().replace(/[-_][a-z]/g, group => group.toUpperCase().replace("_", ""));

const EMPTY_VALUES = {
  work: "",
  nonWorkStart: null,
  nonWorkEnd: null,
  efficiency: "",
  preBuild: false,
  infiniteTool: false,
  lateNotAllowed: false,
  fcfs: false
};

const toDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return dateUtils.dateValidation(parsed) ? parsed : null;
};

const OptionCard = ({ title, description, children }) => (
  <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
    <CardHeader title={title} titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }} sx={{ pb: 0 }} />
    <CardContent sx={{ pt: 1, flex: 1 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>{description}</Typography>
      <Box sx={{ mt: 1 }}>{children}</Box>
    </CardContent>
  </Card>
);

/**
 * 시뮬레이션 옵션 팝업 — old `SimulationOptionPopup.jsx` 대응. 두 가지 모드가 있고 여는 쪽이 고정한다
 * (다이얼로그 내부에 모드 토글은 없다 — old 동일).
 * - `versionCd` 있음 = **확인 모드**: 이력에서 저장된 옵션을 읽기 전용으로 본다. 하단 실행 버튼 없음.
 * - `versionCd` 없음 = **적용 모드**: 상세 그리드의 [옵션] 버튼에서 열린다. 값을 채우고 실행하면
 *   호출부가 플랜 버전 저장 → 옵션 저장 → 엔진 실행 순으로 처리한다.
 *
 * props: open, onClose(), planScope, versionCd, title, onSubmit(List<SimulOptionDto>)
 */
const SimulationOptionPopup = ({ t, open, onClose, planScope, versionCd, title, onSubmit }) => {
  const readOnly = !isNil(versionCd);

  const [values, setValues] = useState(EMPTY_VALUES);
  // 저장 대상은 **변경된 옵션만**이다(old dirtyFields와 동일) — 열었을 때 값을 스냅샷으로 잡아 비교한다.
  const initialValuesRef = useRef(EMPTY_VALUES);

  const setValue = (field, value) => setValues(prev => ({ ...prev, [field]: value }));

  const loadOptions = () => {
    fpSimulationService.getOptions({ planScope, versionCd })
      .then(response => {
        const loaded = { ...EMPTY_VALUES };

        (response.data ?? []).forEach(option => {
          const field = toField(option.categoryCd ?? "");
          const optnVal = option.optnVal;

          if (field === "nonWork") {
            const [start, end] = (optnVal ?? "").split("~");
            loaded.nonWorkStart = toDate(start);
            loaded.nonWorkEnd = toDate(end);
          } else if (SWITCH_FIELDS.includes(field)) {
            loaded[field] = optnVal === "true";
          } else if (OPTION_FIELDS.includes(field)) {
            loaded[field] = optnVal ?? "";
          }
        });

        setValues(loaded);
        initialValuesRef.current = loaded;
      })
      .catch(e => debug(e));
  };

  useEffect(() => {
    if (!open) return;

    if (readOnly && planScope) {
      loadOptions();
      return;
    }

    // 적용 모드는 플랜 버전이 아직 채번되기 전이라 불러올 옵션이 없다(old도 versionCd가 있을 때만 조회).
    setValues(EMPTY_VALUES);
    initialValuesRef.current = EMPTY_VALUES;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, versionCd, planScope]);

  const handleSubmit = () => {
    const initial = initialValuesRef.current;
    const changes = [];

    // 조업 설정(work)은 읽기 전용이라 변경 대상이 아니다(아래 TODO 참고).
    ["efficiency", ...SWITCH_FIELDS].forEach(field => {
      if (values[field] === initial[field]) return;
      changes.push({
        planScope,
        categoryCd: toCategoryCd(field),
        optnVal: SWITCH_FIELDS.includes(field) ? String(values[field]) : `${values[field]}`
      });
    });

    if (values.nonWorkStart !== initial.nonWorkStart || values.nonWorkEnd !== initial.nonWorkEnd) {
      const start = values.nonWorkStart;
      const end = values.nonWorkEnd;
      // 한쪽만 채우면 저장하지 않는다(old rules.validate.required와 동일 판정).
      if ((start && !end) || (!start && end)) {
        onSubmit(null, t("FP_MSG_SELECT_DATE"));
        return;
      }
      changes.push({
        planScope,
        categoryCd: "FP_OPTN_NON_WORK",
        optnVal: (start && end) ? `${dateUtils.formatDate(start, "yyyy-MM-dd")}~${dateUtils.formatDate(end, "yyyy-MM-dd")}` : ""
      });
    }

    if (Number(values.efficiency) < 0) {
      onSubmit(null, t("FP_VALIDATION_LESS_ZERO"));
      return;
    }

    onSubmit(changes);
  };

  const renderSwitch = (field) => (
    <Switch checked={values[field]} disabled={readOnly} onChange={event => setValue(field, event.target.checked)} />
  );

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{`${t(readOnly ? "FP_CONFIRM_OPTION" : "FP_APPLY_OPTION")} ${title ?? ""}`}</DialogTitle>
      <DialogContent dividers>
        {/* TODO(MIG-T2): [simulation/Simulation] 옵션 팝업의 화면 이동 링크 버튼 미이관.
            old: 카드 헤더마다 UI_FP_RESOURCE_CALENDAR / UI_FP_BOR / UI_FP_ORDER / UI_FP_RESOURCE 화면으로
                 history.push(getViewPath(key)) 이동 버튼을 뒀다.
            사유: 대상 4개 화면 중 3개가 미이관이고 ResourceCalendar는 1차 제외 확정(Kendo Scheduler).
                  화면 간 이동은 component-mapping-table.md §8-1에서 Tier2로 확정된 항목이다. */}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <OptionCard title={t("FP_OPTN_WORK")} description={t("FP_OPTN_WORK_DESC_TXT")}>
              {/* TODO(MIG-T2): [simulation/Simulation] 조업 설정(FP_OPTN_WORK) 다중 날짜 선택 미이관.
                  old: flatpickr `mode:'multiple'`로 여러 날짜를 골라 "yyyy-MM-dd, yyyy-MM-dd, ..."로 저장.
                  사유: new 의존성(@mui/x-date-pickers 6.20.2 / react-datepicker 5.1.0) 어느 쪽도 다중 날짜
                        선택을 지원하지 않는다. 화면 하나 때문에 대체 위젯을 신설하지 않는다
                        (10-FORWARD-PLAN.md §6-3). 저장된 값 표시는 읽기 전용으로 이관됨. */}
              <TextField
                fullWidth
                size="small"
                variant="standard"
                value={values.work}
                InputProps={{ readOnly: true }}
                placeholder={t("FP_MSG_SELECT_DATE")}
              />
            </OptionCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <OptionCard title={t("FP_OPTN_NON_WORK")} description={t("FP_OPTN_NON_WORK_DESC_TXT")}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DatePicker
                    label={t("startTs")}
                    format="yyyy-MM-dd"
                    disabled={readOnly}
                    value={values.nonWorkStart}
                    maxDate={values.nonWorkEnd ?? undefined}
                    onChange={value => setValue("nonWorkStart", value)}
                    slotProps={{ textField: { size: "small", sx: { width: 190 } } }}
                  />
                  <DatePicker
                    label={t("endTs")}
                    format="yyyy-MM-dd"
                    disabled={readOnly}
                    value={values.nonWorkEnd}
                    minDate={values.nonWorkStart ?? undefined}
                    onChange={value => setValue("nonWorkEnd", value)}
                    slotProps={{ textField: { size: "small", sx: { width: 190 } } }}
                  />
                </Box>
              </LocalizationProvider>
            </OptionCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <OptionCard title={t("FP_OPTN_EFFICIENCY")} description={t("FP_OPTN_EFFICIENCY_DESC_TXT")}>
              <TextField
                size="small"
                type="number"
                disabled={readOnly}
                value={values.efficiency}
                onChange={event => setValue("efficiency", event.target.value)}
                inputProps={{ min: 0 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ width: 190 }}
              />
            </OptionCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <OptionCard title={t("FP_OPTN_PRE_BUILD")} description={t("FP_OPTN_PRE_BUILD_DESC_TXT")}>
              {renderSwitch("preBuild")}
            </OptionCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <OptionCard title={t("FP_OPTN_INFINITE_TOOL")} description={t("FP_OPTN_INFINITE_TOOL_DESC_TXT")}>
              {renderSwitch("infiniteTool")}
            </OptionCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <OptionCard title={t("FP_OPTN_LATE_NOT_ALLOWED")} description={t("FP_OPTN_LATE_NOT_ALLOWED_DESC_TXT")}>
              {renderSwitch("lateNotAllowed")}
            </OptionCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <OptionCard title={t("FP_OPTN_INFINITE_FCFS")} description={t("FP_OPTN_INFINITE_FCFS_DESC_TXT")}>
              {renderSwitch("fcfs")}
            </OptionCard>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("close")}</Button>
        {!readOnly && (
          <Button variant="contained" onClick={handleSubmit}>{t("EXEC_SIMUL")}</Button>
        )}
      </DialogActions>
    </MuiDialog>
  );
};

export default withTranslation()(SimulationOptionPopup);
